import React, { useContext, useMemo } from "react";
import { FiExternalLink, FiCheck, FiRotateCcw, FiXCircle, FiClock } from "react-icons/fi";
import { ThemeContext } from "../../App";
import { slugify } from "../../utils/slug";
import { collectRevise, scheduleNext, stopRevising, todayKey, REVISE_LADDER } from "../../utils/revision";
import "./revise.css";

// Whole days from today until a YYYY-MM-DD key (negative = overdue).
function daysUntil(key) {
  if (!key) return 0;
  const parts = key.split("-");
  if (parts.length !== 3) return 0;
  const target = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86400000);
}

function whenLabel(key) {
  const d = daysUntil(key);
  if (d <= 0) return "today";
  if (d === 1) return "tomorrow";
  return `in ${d} days`;
}

export default function Revise({ questionData, updateData }) {
  const dark = useContext(ThemeContext);
  const today = todayKey();

  const { due, upcoming, total } = useMemo(
    () => collectRevise(questionData, today),
    [questionData, today]
  );

  // Patch a single question (by topic/problem index) and persist via updateData.
  function patch(ti, pi, changes) {
    const topic = questionData[ti];
    if (!topic) return;
    const questions = topic.questions.map((q, i) => (i === pi ? { ...q, ...changes } : q));
    const done = questions.filter((q) => q.Done).length;
    updateData(slugify(topic.topicName), { started: done > 0, doneQuestions: done, questions }, ti);
  }

  const gotIt = (item) => patch(item.ti, item.pi, scheduleNext(item.q.ReviseStage, "good"));
  const shaky = (item) => patch(item.ti, item.pi, scheduleNext(item.q.ReviseStage, "again"));
  const stop = (item) => patch(item.ti, item.pi, stopRevising());

  return (
    <div className={`revise-page ${dark ? "revise-dark" : ""}`}>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="repeat">
          🔁
        </div>
        <h1 className="page-title">Revise</h1>
        <p className="page-sub">
          Spaced repetition for problems you flagged. Review the ones due today; each “Got it”
          pushes the next review further out ({REVISE_LADDER.join(", ")} days).
        </p>
      </div>

      {total === 0 ? (
        <div className="rv-empty">
          <div className="rv-empty-emoji">🗂️</div>
          <p>
            Nothing queued yet. Hit <strong>Mark for revision</strong> on any problem in Practice or a
            topic, and it’ll show up here on a spaced schedule.
          </p>
        </div>
      ) : (
        <>
          <div className="rv-summary">
            <div className="rv-chip">
              <span className="rv-chip-num">{due.length}</span>
              <span className="rv-chip-lbl">due today</span>
            </div>
            <div className="rv-chip">
              <span className="rv-chip-num">{upcoming.length}</span>
              <span className="rv-chip-lbl">scheduled</span>
            </div>
            <div className="rv-chip">
              <span className="rv-chip-num">{total}</span>
              <span className="rv-chip-lbl">in rotation</span>
            </div>
          </div>

          <div className="rv-section">
            <h5 className="rv-h5">Due today</h5>
            {due.length === 0 ? (
              <div className="rv-caught">
                <FiCheck size={16} /> All caught up — nothing due right now. Come back when the next
                review lands.
              </div>
            ) : (
              <div className="rv-list">
                {due.map((item) => {
                  const q = item.q;
                  const stage = q.ReviseStage > 0 ? q.ReviseStage : 0;
                  return (
                    <div className="rv-card" key={`${item.ti}-${item.pi}`}>
                      <div className="rv-card-top">
                        <span className="nb-pill gray">{item.topicName}</span>
                        {q.Difficulty ? <span className="nb-pill gray">{q.Difficulty}</span> : null}
                        <span className="rv-stage">Stage {stage + 1}/{REVISE_LADDER.length}</span>
                      </div>
                      <div className="rv-card-name">{q.Problem}</div>
                      {q.Notes && q.Notes.length ? <div className="rv-notes">{q.Notes}</div> : null}
                      <div className="rv-actions">
                        {q.URL ? (
                          <a className="nb-btn nb-btn-sm" href={q.URL} target="_blank" rel="noopener noreferrer">
                            <FiExternalLink size={14} /> Open
                          </a>
                        ) : null}
                        <button type="button" className="nb-btn nb-btn-primary nb-btn-sm" onClick={() => gotIt(item)}>
                          <FiCheck size={14} /> Got it
                        </button>
                        <button type="button" className="nb-btn nb-btn-sm" onClick={() => shaky(item)}>
                          <FiRotateCcw size={14} /> Still shaky
                        </button>
                        <button type="button" className="nb-btn nb-btn-sm rv-stop" onClick={() => stop(item)}>
                          <FiXCircle size={14} /> Stop
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {upcoming.length ? (
            <div className="rv-section">
              <h5 className="rv-h5">Coming up</h5>
              <div className="rv-upcoming">
                {upcoming.map((item) => (
                  <div className="rv-up-row" key={`${item.ti}-${item.pi}`}>
                    <span className="rv-up-name" title={item.q.Problem}>{item.q.Problem}</span>
                    <span className="rv-up-topic">{item.topicName}</span>
                    <span className="rv-up-when">
                      <FiClock size={12} /> {whenLabel(item.q.ReviseAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
